import { Check, Clock } from "lucide-react";

import { Badge } from "@bcc/components/ui/badge";
import { cn } from "@bcc/lib/utils";

import type { IntensityConfidence } from "./intensity-scale.types";
import { setIntensityConfidenceBadgeTestId } from "./intensity-scale.utils";

interface IntensityConfidenceBadgeProps {
  confidence: IntensityConfidence;
  /** Text and icon label for an approved (PIM) score. */
  approvedLabel: string;
  /** Text and icon label for a provisional (Salesforce) score. */
  provisionalLabel: string;
  className?: string;
  testId?: string;
}

/**
 * Small pill marking whether a coffee's score has been signed off in PIM
 * (green, tick) or is still the roaster's provisional Salesforce value (blue,
 * clock). Shared between the "most similar to" comparison dropdown and the
 * scale's marker tooltips so the same colour and glyph mean the same thing
 * everywhere a comparison coffee is named.
 */
function IntensityConfidenceBadge({
  confidence,
  approvedLabel,
  provisionalLabel,
  className,
  testId,
}: Readonly<IntensityConfidenceBadgeProps>) {
  const isApproved = confidence === "approved";
  const label = isApproved ? approvedLabel : provisionalLabel;

  return (
    <Badge
      variant="outline"
      className={cn(
        "shrink-0",
        isApproved ? "border-transparent bg-green-100 text-green-900" : "border-transparent bg-blue-100 text-blue-900",
        className
      )}
      testId={testId ?? setIntensityConfidenceBadgeTestId(confidence)}
    >
      {/* Decorative: the visible label text next to it already carries the meaning. */}
      {isApproved ? <Check aria-hidden className="size-3" /> : <Clock aria-hidden className="size-3" />}
      {label}
    </Badge>
  );
}

export { IntensityConfidenceBadge };
